import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  Input,
  Skeleton,
  Tag,
  Typography,
} from 'antd';

import { MedicalDictionaryType } from '@app/api/Dictionaries';
import { FormItemField, InputField } from '@app/components/FormFields';
import { emptyFn, OFF, PREFIX } from '@app/constants';
import {
  getDocumentStatusColor,
  getDocumentStatusLabel,
} from '@app/pages/MedicalCard/MedicalCardRegistry/types';

import {
  CreateTemplateModal,
  DictionarySelect,
  NumberField,
  SelectField,
  SignDocumentModal,
  SnilsInput,
  TextAreaField,
} from '../components';
import {
  diagnosisNatureOptions,
  diagnosisTypeOptions,
  signerRoleOptions,
  generalConditionOptions,
  treatmentRegimenOptions,
} from '../data/dictionaries';
import { useMedicalCard, useMedicalCardEffects } from '../hooks';

import '@app/components/RecordModal/components/MedicalCard/styles/MedicalCard.desktop.scss';

const { Title } = Typography;

export const MedicalCard = () => {
  const {
    form,
    handleFinish,
    isLoading,
    isFormDisabled,
    isPendingSignStatus,
    isDoctorSigned,
    isChiefSigned,
    canSubmitForSigning,
    canSignAsDoctor,
    canSignAsChief,
    canSendToMedFlex,
    isResendToMedFlex,
    signingRole,
    openSigning,
    closeSigning,
    handleSigned,
    handleSubmitForSigning,
    medicalDocument,
    handleSendToMedFlex,
    setInitialData,
    getClientInfo,
    getDoctorInfo,
    isGetPresetDataLoading,
    patientData,
    initialData,
    doctorData,
  } = useMedicalCard();
  const { isEffectsLoading } = useMedicalCardEffects({
    setInitialData,
    getClientInfo,
    getDoctorInfo,
  });

  const isGetDataRequestsLoading = isGetPresetDataLoading || isEffectsLoading;

  const isWaitingChiefSignature =
    isPendingSignStatus && isDoctorSigned && !isChiefSigned;

  return (
    <div className={`${PREFIX}medical-card`}>
      <Title level={4} className="page-title">
        Медицинская карта
        {medicalDocument && (
          <Tag
            color={getDocumentStatusColor(medicalDocument.Status)}
            className="status-tag"
          >
            {isWaitingChiefSignature
              ? 'Ожидает подписи организации'
              : getDocumentStatusLabel(medicalDocument.Status)}
          </Tag>
        )}
      </Title>

      <Form
        form={form}
        layout="vertical"
        clearOnDestroy
        autoComplete={OFF}
        className={`${PREFIX}form`}
        onFinish={handleFinish}
        disabled={isFormDisabled}
      >
        {isGetDataRequestsLoading ? (
          <Skeleton />
        ) : (
          <>
            {/* Пока поддерживаем только 1 документ. Временно скрываем */}
            {/* <Field label="Код вида документа из НСИ" name="Kind" required>
          <Input size="large" maxLength={100} />
        </Field> */}
            <Title level={5} className="card-title">
              Данные пациента
            </Title>
            <Card title="Данные пациента" className="diagnosis-card">
              <InputField
                label="Фамилия"
                fieldName={['Patient', 'Surname']}
                required
                disabled={isFormDisabled}
                maxLength={100}
                size="large"
                validateStatus="warning"
              />

              <InputField
                label="Имя"
                fieldName={['Patient', 'Name']}
                required
                validateStatus="warning"
                disabled={isFormDisabled}
                maxLength={100}
                size="large"
              />

              <InputField
                label="Отчество"
                fieldName={['Patient', 'Patronymic']}
                disabled={isFormDisabled}
                maxLength={100}
                size="large"
              />

              <FormItemField
                label="Дата рождения"
                fieldName={['Patient', 'BirthDate']}
                required
                validateStatus="warning"
              >
                <DatePicker
                  placeholder="Выберите дату рождения"
                  format="DD.MM.YYYY"
                  style={{ display: 'block' }}
                  size="large"
                  disabled={isFormDisabled}
                />
              </FormItemField>

              <SnilsInput
                fieldName={['Patient', 'Snils']}
                label="СНИЛС пациента (11 цифр)"
                disabled={isFormDisabled}
                required
                validateStatus="warning"
              />

              <DictionarySelect
                type={MedicalDictionaryType.Genders}
                title="Пол"
                required
                validateStatus="warning"
                disabled={isFormDisabled}
                name={['Patient', 'Gender']}
                placeholder=""
                isStatic
                initialValue={form.getFieldValue(['Patient', 'Gender'])}
              />

              <InputField
                label="Контактный телефон"
                fieldName={['Patient', 'Phone']}
                disabled={isFormDisabled}
                maxLength={20}
                size="large"
              />

              <DictionarySelect
                disabled={isFormDisabled}
                type={MedicalDictionaryType.IdentityDocuments}
                title="Тип документа"
                name={['Patient', 'Document', 'Type']}
                isStatic
                placeholder=""
                initialValue={patientData?.Document?.Type}
              />

              <InputField
                label="Номер документа"
                fieldName={['Patient', 'Document', 'Number']}
                size="large"
                disabled={isFormDisabled}
                maxLength={20}
              />

              <TextAreaField
                label="Адрес регистрации пациента"
                fieldName={['Patient', 'Address']}
                rows={2}
                disabled={isFormDisabled}
                maxLength={500}
              />
            </Card>
            <Title level={5} className="card-title">
              Клинические данные медицинского документа
            </Title>
            <Card title="Диагноз" className="diagnosis-card">
              <DictionarySelect
                type={MedicalDictionaryType.Icd10}
                title="Диагноз"
                required
                validateStatus="warning"
                name={['ClinicalData', 'PrimaryDiagnosis', 'Code']}
                disabled={isFormDisabled}
                initialValue={
                  initialData[0]?.ClinicalData?.PrimaryDiagnosis?.Code ??
                  undefined
                }
              />

              <TextAreaField
                label="Наименование диагноза"
                fieldName={['ClinicalData', 'PrimaryDiagnosis', 'Description']}
                disabled={isFormDisabled}
                maxLength={300}
              />

              <SelectField
                label="Тип диагноза"
                fieldName={['ClinicalData', 'PrimaryDiagnosis', 'Type']}
                placeholder="Выберите тип диагноза"
                options={diagnosisTypeOptions}
                disabled={isFormDisabled}
                size="large"
              />

              <SelectField
                label="Характер заболевания"
                fieldName={['ClinicalData', 'PrimaryDiagnosis', 'Nature']}
                placeholder="Выберите характер заболевания"
                options={diagnosisNatureOptions}
                disabled={isFormDisabled}
                size="large"
              />
            </Card>
            <Form.List
              name={['ClinicalData', 'SecondaryDiagnoses']}
              initialValue={[]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Card
                      key={field.key}
                      title={`Сопутствующий диагноз ${field.name + 1}`}
                      className="diagnosis-card"
                      extra={
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      }
                    >
                      <DictionarySelect
                        type={MedicalDictionaryType.Icd10}
                        title="Диагноз"
                        required
                        validateStatus="warning"
                        disabled={isFormDisabled}
                        name={[field.name, 'Code']}
                        initialValue={
                          initialData[0]?.ClinicalData?.SecondaryDiagnoses?.[
                            field.name
                          ]?.Code ?? undefined
                        }
                      />

                      <TextAreaField
                        label="Наименование диагноза"
                        fieldName={[field.name, 'Description']}
                        disabled={isFormDisabled}
                        maxLength={300}
                      />

                      <SelectField
                        label="Тип диагноза"
                        fieldName={[field.name, 'Type']}
                        placeholder="Выберите тип диагноза"
                        options={diagnosisTypeOptions}
                        disabled={isFormDisabled}
                      />

                      <SelectField
                        label="Характер заболевания"
                        fieldName={[field.name, 'Nature']}
                        placeholder="Выберите характер заболевания"
                        options={diagnosisNatureOptions}
                        disabled={isFormDisabled}
                      />
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    className="action-button"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Добавить сопутствующий диагноз
                  </Button>
                </>
              )}
            </Form.List>
            <Card title="Жалобы пациента" className="diagnosis-card">
              <TextAreaField
                label="Жалобы пациента"
                fieldName={['ClinicalData', 'Complaints']}
                disabled={isFormDisabled}
                maxLength={3000}
              />
            </Card>
            <Card title="Анамнез" className="diagnosis-card">
              <TextAreaField
                label="История заболевания"
                fieldName={['ClinicalData', 'Anamnesis', 'DiseaseHistory']}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="История жизни"
                fieldName={['ClinicalData', 'Anamnesis', 'LifeHistory']}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="Аллергологический анамнез"
                fieldName={['ClinicalData', 'Anamnesis', 'AllergyHistory']}
                disabled={isFormDisabled}
                maxLength={3000}
              />
            </Card>
            <Card title="Объективный статус" className="diagnosis-card">
              <SelectField
                label="Общее состояние"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'GeneralCondition',
                ]}
                placeholder="Выберите общее состояние"
                options={generalConditionOptions}
                disabled={isFormDisabled}
                size="large"
              />

              <NumberField
                label="Температура"
                fieldName={['ClinicalData', 'ObjectiveStatus', 'Temperature']}
                form={form}
                min={0.01}
                max={42}
                disabled={isFormDisabled}
                size="large"
              />

              <NumberField
                label="Систолическое давление"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'BloodPressure',
                  'Systolic',
                ]}
                form={form}
                disabled={isFormDisabled}
                size="large"
              />

              <NumberField
                label="Диастолическое давление"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'BloodPressure',
                  'Diastolic',
                ]}
                form={form}
                disabled={isFormDisabled}
                max={200}
                size="large"
              />

              <NumberField
                label="Пульс"
                fieldName={['ClinicalData', 'ObjectiveStatus', 'HeartRate']}
                form={form}
                disabled={isFormDisabled}
                max={400}
                size="large"
              />

              <NumberField
                label="Частота дыхания"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'RespiratoryRate',
                ]}
                form={form}
                disabled={isFormDisabled}
                max={200}
                size="large"
              />

              <TextAreaField
                label="Кожа"
                fieldName={['ClinicalData', 'ObjectiveStatus', 'Skin']}
                rows={3}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="Дыхательная система"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'RespiratorySystem',
                ]}
                rows={3}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="Сердечно-сосудистая система"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'CardiovascularSystem',
                ]}
                rows={3}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="Пищеварительная система"
                fieldName={[
                  'ClinicalData',
                  'ObjectiveStatus',
                  'DigestiveSystem',
                ]}
                rows={3}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <TextAreaField
                label="Локальный статус"
                fieldName={['ClinicalData', 'ObjectiveStatus', 'LocalStatus']}
                disabled={isFormDisabled}
                maxLength={3000}
              />
            </Card>
            <Card title="Лечение / Рекомендации" className="diagnosis-card">
              <TextAreaField
                label="Рекомендации"
                fieldName={['ClinicalData', 'Treatment', 'Recommendations']}
                rows={5}
                disabled={isFormDisabled}
                maxLength={3000}
              />

              <Form.List
                name={['ClinicalData', 'Treatment', 'Medications']}
                initialValue={[]}
              >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        title={`Препарат ${field.name + 1}`}
                        className="diagnosis-card"
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        }
                      >
                        <TextAreaField
                          label="Наименование препарата"
                          fieldName={[field.name, 'Name']}
                          disabled={isFormDisabled}
                          maxLength={500}
                        />

                        <TextAreaField
                          label="Дозировка"
                          fieldName={[field.name, 'Dosage']}
                          disabled={isFormDisabled}
                          maxLength={500}
                        />

                        <TextAreaField
                          label="Способ введения"
                          fieldName={[field.name, 'AdministrationRoute']}
                          disabled={isFormDisabled}
                          maxLength={3000}
                        />

                        <TextAreaField
                          label="Частота приема"
                          fieldName={[field.name, 'Frequency']}
                          disabled={isFormDisabled}
                          maxLength={3000}
                        />

                        <TextAreaField
                          label="Продолжительность"
                          fieldName={[field.name, 'Duration']}
                          disabled={isFormDisabled}
                          maxLength={3000}
                        />
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      className="action-button"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Добавить препарат
                    </Button>
                  </>
                )}
              </Form.List>

              <Form.List
                name={['ClinicalData', 'Treatment', 'Procedures']}
                initialValue={[]}
              >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        title={`Процедура ${field.name + 1}`}
                        className="diagnosis-card"
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        }
                      >
                        <TextAreaField
                          label="Наименование процедуры"
                          fieldName={[field.name, 'Name']}
                          disabled={isFormDisabled}
                          maxLength={3000}
                        />

                        <FormItemField
                          label="Количество"
                          fieldName={[field.name, 'Quantity']}
                          required
                          validateStatus="warning"
                        >
                          <Input
                            size="large"
                            maxLength={100}
                            disabled={isFormDisabled}
                            onBeforeInput={(e) => {
                              if (!/[0-9]/.test(e.data)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </FormItemField>

                        <TextAreaField
                          label="Примечание"
                          fieldName={[field.name, 'Note']}
                          disabled={isFormDisabled}
                          maxLength={3000}
                        />
                      </Card>
                    ))}

                    <Button
                      type="dashed"
                      className="action-button"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Добавить Процедуру
                    </Button>
                  </>
                )}
              </Form.List>

              <SelectField
                label="Режим лечения"
                fieldName={['ClinicalData', 'Treatment', 'Regimen']}
                placeholder="Выберите режим лечения"
                options={treatmentRegimenOptions}
                disabled={isFormDisabled}
              />

              <TextAreaField
                label="Дальнейшее наблюдение"
                fieldName={['ClinicalData', 'Treatment', 'FollowUp']}
                disabled={isFormDisabled}
                maxLength={3000}
              />
            </Card>
            <Title level={5} className="card-title">
              Данные врача
            </Title>
            <Card title="Данные врача" className="diagnosis-card">
              <InputField
                label="Фамилия"
                fieldName={['Doctor', 'Surname']}
                required
                validateStatus="warning"
                disabled={isFormDisabled}
                maxLength={100}
                size="large"
              />

              <InputField
                size="large"
                label="Имя"
                fieldName={['Doctor', 'Name']}
                required
                validateStatus="warning"
                disabled={isFormDisabled}
                maxLength={100}
              />

              <InputField
                size="large"
                label="Отчество"
                fieldName={['Doctor', 'Patronymic']}
                disabled={isFormDisabled}
                maxLength={100}
              />

              <FormItemField
                label="Дата рождения"
                fieldName={['Doctor', 'BirthDate']}
              >
                <DatePicker
                  placeholder="Выберите дату рождения"
                  format="DD.MM.YYYY"
                  style={{ display: 'block' }}
                  size="large"
                  disabled={isFormDisabled}
                />
              </FormItemField>

              <SnilsInput
                fieldName={['Doctor', 'Snils']}
                label="СНИЛС врача (11 цифр)"
                disabled={isFormDisabled}
                required
                validateStatus="warning"
              />

              <SelectField
                label="Должность"
                fieldName={['Doctor', 'Role']}
                placeholder="Выберите должность"
                options={signerRoleOptions}
                required
                validateStatus="warning"
                disabled={isFormDisabled}
                size="large"
              />

              <DictionarySelect
                disabled={isFormDisabled}
                type={MedicalDictionaryType.Positions}
                title="Код должности врача по НСИ"
                name={['Doctor', 'PositionCode']}
                initialValue={doctorData?.PositionCode}
              />

              <DictionarySelect
                disabled={isFormDisabled}
                type={MedicalDictionaryType.Specialities}
                title="Код специальности из НСИ"
                name={['Doctor', 'Speciality']}
                initialValue={doctorData?.Speciality}
              />
            </Card>
            <Flex justify="space-between" className="action-buttons">
              {!isFormDisabled && (
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={isLoading}
                >
                  Сохранить
                </Button>
              )}
              {/* Кнопки действий работают и при заблокированной форме:
                  явный disabled перекрывает DisabledContext от Form. */}
              {canSubmitForSigning && (
                <Button
                  size="large"
                  onClick={handleSubmitForSigning}
                  loading={isLoading}
                  disabled={false}
                >
                  Отправить на подпись
                </Button>
              )}
              {canSignAsDoctor && (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => openSigning('DOCTOR')}
                  disabled={false}
                >
                  Подписать
                </Button>
              )}
              {canSignAsChief && (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => openSigning('CHIEF')}
                  disabled={false}
                >
                  Подписать за организацию
                </Button>
              )}
              {canSendToMedFlex && (
                <Button
                  size="large"
                  onClick={handleSendToMedFlex}
                  loading={isLoading}
                  disabled={false}
                >
                  {isResendToMedFlex
                    ? 'Повторить отправку в МедФлекс'
                    : 'Отправить в МедФлекс'}
                </Button>
              )}
            </Flex>
          </>
        )}
      </Form>

      <SignDocumentModal
        documentId={medicalDocument?.Id ?? null}
        role={signingRole}
        onClose={closeSigning}
        onSigned={handleSigned}
      />

      <CreateTemplateModal open={false} onCancel={emptyFn} onCreate={emptyFn} />
    </div>
  );
};
